/**
 * Shared API URL and utility functions for Bon de Travail components
 */

export const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Taux de taxe QC
const TAUX_TAXE_QC = 1.14975;
// % du contrat alloué à la main d'oeuvre
const TAUX_MAIN_OEUVRE = 0.45;

/**
 * Heures par jour selon la saison.
 * Été (mai–sept) = 8h, Hiver (oct–avril) = 7.5h
 */
export function getHeuresParJour(dates) {
  let mois;
  if (dates && dates.length > 0) {
    const d = new Date(dates[0] + 'T12:00:00');
    mois = d.getMonth();
  } else {
    mois = new Date().getMonth();
  }
  return (mois >= 4 && mois <= 8) ? 8 : 7.5;
}

/**
 * Retourne le coût employeur d'un employé à une date donnée.
 * Cherche dans historique_salaires, sinon utilise salaires.cout_employeur actuel.
 */
export function getCoutEmployeur(employee, dateRef) {
  // 1. Coût actuel comme fallback
  const coutActuel = parseFloat(employee?.salaires?.cout_employeur || 0);
  
  // 2. Si pas d'historique ou pas de date, retourner le coût actuel
  const historique = employee?.historique_salaires;
  if (!historique || !Array.isArray(historique) || historique.length === 0) {
    return coutActuel;
  }
  if (!dateRef) return coutActuel;
  
  const ref = dateRef.substring(0, 10);
  
  // 3. Trier par date_effective décroissante
  const sorted = historique
    .filter(h => h && h.date_effective)
    .sort((a, b) => b.date_effective.localeCompare(a.date_effective));
  
  // 4. Trouver la première entrée dont date_effective <= dateRef
  for (const aug of sorted) {
    if (aug.date_effective.substring(0, 10) <= ref) {
      // Chercher le coût employeur dans cette entrée
      const cout = parseFloat(aug.cout_employeur) || parseFloat(aug.nouveau_taux) || 0;
      if (cout > 0) return cout;
    }
  }
  
  // 5. Fallback: coût actuel
  return coutActuel;
}

/**
 * Calcule le coût moyen par employé d'une équipe (chef + ses peintres).
 * Retourne { coutMoyen, coutTotal, nbMembres, membres }
 *
 * 🚀 [Phase 107.B — 2026-07-14] Suppression du système d'équipes prédéterminées.
 *   Avant : filtrait `allEmployees.filter(e => e.chef_equipe_id === chefId)`.
 *   Maintenant : considère TOUS les employés terrain actifs (peintres + chefs
 *   d'équipe), moins les sous-traitants/installateurs. L'ancien `chefId` reste
 *   accepté pour rétrocompat mais n'est plus utilisé comme filtre.
 *
 *   Note : ce calcul frontend sert UNIQUEMENT à donner une estimation à l'écran
 *   au moment de la création/édition. Pour la source de vérité (audit paie),
 *   utiliser l'endpoint backend `GET /api/crm/bons-travail/{bt_id}/cout-employeur-dynamique`.
 */
export function calculerCoutEquipe(chefId, allEmployees, dateChantier) {
  if (!allEmployees || !allEmployees.length) {
    return { coutMoyen: 0, coutTotal: 0, nbMembres: 0, membres: [] };
  }

  // 🚀 [Phase 107.B] Considérer TOUS les employés terrain actifs
  //   (peintres + chefs d'équipe), sauf sous-traitants/installateurs.
  // 🗑️ [v13.60.1 — 2026-08-05] Filtre `est_specialiste_lavage` retiré (P2 RCA).
  const ROLES_EXCLUS = ['sous_traitant', 'installateur'];
  const terrainActifs = allEmployees.filter(e => {
    if (e.est_actif === false) return false;
    const rt = String(e.role_terrain || '').toLowerCase();
    const te = String(e.type_employe || '').toLowerCase();
    if (ROLES_EXCLUS.includes(rt) || ROLES_EXCLUS.includes(te)) return false;
    // Ne garder que peintre + chef_equipe (rôles terrain valides pour coût MO)
    return rt === 'peintre' || rt === 'chef_equipe' || te === 'peintre' || te === 'chef_equipe';
  });

  if (terrainActifs.length === 0) {
    return { coutMoyen: 0, coutTotal: 0, nbMembres: 0, membres: [] };
  }

  let coutTotal = 0;
  const details = [];
  for (const m of terrainActifs) {
    const cout = getCoutEmployeur(m, dateChantier);
    coutTotal += cout;
    details.push({ id: m.id, nom: `${m.prenom || ''} ${m.nom || ''}`.trim(), cout });
  }

  const nbMembres = terrainActifs.length;
  const coutMoyen = nbMembres > 0 ? coutTotal / nbMembres : 0;

  return { coutMoyen, coutTotal, nbMembres, membres: details };
}

/**
 * Calcule les heures budgétées pour une job.
 * 
 * Formule: montant_contrat_HT × 45% ÷ coût_moyen_par_employé = heures totales
 * Jours = heures totales ÷ (heures_par_jour × nb_employés)
 */
export function calculerHeuresBudgetees(montantContratTTC, coutMoyenParEmploye, nbMembres, dates) {
  if (!montantContratTTC || montantContratTTC <= 0) {
    return { heures: 0, jours: 0, heuresParJour: 0, saison: '', montantHT: 0, coutMoyen: 0 };
  }
  
  // Convertir TTC → HT
  const montantHT = montantContratTTC / TAUX_TAXE_QC;
  
  // Budget main d'oeuvre = 45% du HT
  const budgetMO = montantHT * TAUX_MAIN_OEUVRE;
  
  // Si pas de coût moyen, impossible de calculer
  if (!coutMoyenParEmploye || coutMoyenParEmploye <= 0) {
    return { heures: 0, jours: 0, heuresParJour: 0, saison: '', montantHT: Math.round(montantHT), coutMoyen: 0 };
  }
  
  // Heures totales = budget MO ÷ coût moyen d'UN employé
  const heures = Math.round(budgetMO / coutMoyenParEmploye);
  
  // Jours
  const heuresParJour = getHeuresParJour(dates);
  const saison = heuresParJour === 8 ? 'été' : 'hiver';
  const jours = (nbMembres > 0 && heuresParJour > 0)
    ? Math.ceil((heures / (heuresParJour * nbMembres)) * 10) / 10
    : 0;
  
  return { 
    heures, jours, heuresParJour, saison, 
    montantHT: Math.round(montantHT), 
    coutMoyen: Math.round(coutMoyenParEmploye * 100) / 100 
  };
}

/**
 * Calculate Haversine distance between two GPS coordinates
 * @returns distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
